import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { DonutChart, type DonutSlice } from "./charts";
import { Segmented } from "./dashboard";
import { ArrowRight, Download, Eye, Printer } from "./icons";
import { deltaText, duration, num } from "../lib/format";
import type { StatsBreakdownRow, StatsSummary } from "../lib/api";
import { SLOW_THRESHOLD_SECONDS, type DocTypeRow } from "../lib/dashboard-data";
import { DX_PALETTE, OTHER_COLOR, isIdle, moneyParts, moneyText } from "../lib/dx";

/**
 * ชิ้นส่วน Dashboard ตาม SDS-Admin-Mockup v3 (`js/screens/dx_core.js` + `dx_overview.js`)
 *
 * ตัวเลขต่อพื้นที่มาจาก /stats/breakdown แถวเดียวกันทุกการ์ด (จัดอันดับ · ตาราง · แผนที่ · เรื่องค้าง)
 * แถวรวมมาจาก /stats/summary ซึ่ง server รับประกันว่า = Σ แถว
 *
 * ต่างจาก mockup โดยตั้งใจ (กฎ console honesty — ไม่มีข้อมูลจริงก็บอกว่าไม่มี):
 * - "ຍື່ນຜ່ານແອັບ %" แสดงช่องแต่เป็น "ຍັງບໍ່ມີຂໍ້ມູນ" (ระบบไม่ได้บันทึกช่องทางยื่น)
 * - เงินใช้ป้าย "ຄ່າທຳນຽມທີ່ຊຳລະແລ້ວ" ไม่ใช่ "ລາຍຮັບ (ມີໃບຮັບເງິນ)"
 * - "ເປີດໃຊ້ແລ້ວ" ของตาแสง = มีเจ้าหน้าที่ประจำตาแสงอย่างน้อยหนึ่งคน (ไม่มีสถานะเปิด/ปิดตาแสงในระบบ)
 */

/** แถบบนสุด: ตัวกรองช่วงเวลา ซ้าย · ปุ่มรายงาน/พิมพ์/ส่งออก ขวา */
export function DashBar({ children, tools }: { children: ReactNode; tools?: ReactNode }) {
  return (
    <div className="dx-bar no-print">
      <div className="dx-bar__filters">{children}</div>
      {tools ? <div className="dx-bar__tools">{tools}</div> : null}
    </div>
  );
}

/** ปุ่ม ເບິ່ງຕົວຢ່າງລາຍງານ / ພິມ / PDF / CSV */
export function DashTools({ onPreview, onCsv }: { onPreview?: () => void; onCsv?: () => void }) {
  return (
    <>
      {onPreview ? (
        <button type="button" className="btn-primary" onClick={onPreview}>
          <Eye size={14} /> ເບິ່ງຕົວຢ່າງລາຍງານ
        </button>
      ) : null}
      <button type="button" className="btn-secondary" onClick={() => window.print()}>
        <Printer size={14} /> ພິມ
      </button>
      <button
        type="button"
        className="btn-secondary"
        title="ເລືອກ ບັນທຶກເປັນ PDF ໃນໜ້າຕ່າງພິມ"
        onClick={() => window.print()}
      >
        <Download size={14} /> ສົ່ງອອກ PDF
      </button>
      <button type="button" className="btn-secondary" onClick={onCsv} disabled={!onCsv}>
        <Download size={14} /> ສົ່ງອອກ CSV
      </button>
    </>
  );
}

export type DxTone = "navy" | "green" | "gold" | "red" | "ink" | "teal";

/** การ์ด KPI สีอ่อนตาม tone: ตัวเลขใหญ่ + หน่วย + เทียบช่วงก่อน (ไม่มีเส้นเล็กในการ์ด ตาม mockup) */
export function KpiTile({
  label,
  value,
  unit,
  tone,
  deltaPct,
  hint,
  to,
  na,
}: {
  label: string;
  /** `undefined` = กำลังโหลด */
  value: string | undefined;
  unit?: string;
  tone: DxTone;
  /** `undefined` = การ์ดนี้ไม่มีการเทียบ · `null` = ช่วงก่อนเป็นศูนย์ ไม่มีฐาน (ห้ามแสดง 0%) */
  deltaPct?: number | null;
  hint?: ReactNode;
  to?: string;
  /** ไม่มีข้อมูลรองรับในระบบ — แสดงช่องแต่บอกเหตุผล ห้ามใส่ตัวเลข */
  na?: string;
}) {
  const delta = deltaText(deltaPct);
  const body = (
    <>
      <div className="dx-kpi__label">{label}</div>
      {na ? (
        <>
          <div className="dx-kpi__value">
            <span className="num">—</span>
            <span className="dx-kpi__unit">ຍັງບໍ່ມີຂໍ້ມູນ</span>
          </div>
          <div className="dx-kpi__foot">
            <span className="dx-kpi__hint">{na}</span>
          </div>
        </>
      ) : (
        <>
          <div className="dx-kpi__value">
            <span className="num">{value ?? "—"}</span>
            {unit && value !== undefined ? <span className="dx-kpi__unit">{unit}</span> : null}
          </div>
          <div className="dx-kpi__foot">
            {deltaPct === undefined ? null : delta ? (
              <span className={delta.direction === "up" ? "dx-kpi__delta is-up" : "dx-kpi__delta is-down"}>
                <span className="num">{delta.text}</span> ທຽບຊ່ວງກ່ອນ
              </span>
            ) : (
              <span className="dx-kpi__delta" title="ຊ່ວງກ່ອນໜ້າເປັນສູນ ບໍ່ມີຖານໃຫ້ທຽບ">
                — ບໍ່ມີຖານທຽບ
              </span>
            )}
            {hint ? <span className="dx-kpi__hint">{hint}</span> : null}
          </div>
        </>
      )}
    </>
  );
  const cls = `dx-kpi dx-kpi--${na ? "ink" : tone}${na ? " is-na" : ""}`;
  return to && !na ? (
    <Link to={to} className={`${cls} is-link`}>
      {body}
    </Link>
  ) : (
    <article className={cls}>{body}</article>
  );
}

/** หัวข้อของส่วน (ใหญ่กว่าหัวการ์ด) */
export function DxSection({ title, sub, right }: { title: string; sub?: string; right?: ReactNode }) {
  return (
    <div className="dx-sec">
      <h2 className="dx-sec__title">{title}</h2>
      {sub ? <span className="dx-sec__sub">{sub}</span> : null}
      <span className="dx-sec__spacer" />
      {right}
    </div>
  );
}

/** การ์ดของ Dashboard: หัว + คำอธิบาย + ปุ่มขวา */
export function DxCard({
  title,
  sub,
  right,
  flush,
  children,
}: {
  title: string;
  sub?: string;
  right?: ReactNode;
  /** true = เนื้อหาชิดขอบ (ตาราง) */
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={flush ? "dx-card dx-card--flush" : "dx-card"}>
      {title || sub || right ? (
        <header className="dx-card__head">
          <div>
            {title ? <h2 className="dx-card__title">{title}</h2> : null}
            {sub ? <p className="dx-card__sub">{sub}</p> : null}
          </div>
          {right ? <div className="dx-card__right">{right}</div> : null}
        </header>
      ) : null}
      <div className="dx-card__body">{children}</div>
    </section>
  );
}

interface RankRow {
  key: string;
  label: string;
  sub?: string;
  /** null = ยังไม่เปิดใช้ (ไม่ใช่ 0) — อยู่ท้ายรายการ ไม่มีแถบ */
  value: number | null;
  text: string;
  share: number | null;
  color: string;
  onClick?: () => void;
}

/** รายการจัดอันดับแบบแถบนอน: เลขอันดับ · ชื่อ · แถบ · ค่า + สัดส่วน */
export function RankList({ rows, emptyLabel }: { rows: RankRow[]; emptyLabel: string }) {
  const max = Math.max(0, ...rows.map((r) => r.value ?? 0));
  if (rows.length === 0) return <p className="dx-empty">{emptyLabel}</p>;
  // เลขอันดับนับเฉพาะแถวที่มีค่า — แถว "ຍັງບໍ່ເປີດໃຊ້" ไม่มีอันดับ
  const ranks = rows.map((r, i) => (r.value === null ? 0 : rows.slice(0, i + 1).filter((x) => x.value !== null).length));
  return (
    <ol className="dx-rk">
      {rows.map((r, i) => {
        const rank = ranks[i] ?? 0;
        const inner = (
          <>
            <span className="dx-rk__no num">{r.value === null ? "" : rank}</span>
            <span className="dx-rk__name">
              {r.label}
              {r.sub ? <small>{r.sub}</small> : null}
            </span>
            <span className="dx-rk__track">
              {r.value !== null && max > 0 ? (
                <span
                  className="dx-rk__bar"
                  style={{ width: `${String(Math.max(3, (r.value / max) * 100))}%`, background: r.color }}
                />
              ) : null}
            </span>
            <span className={r.value === null ? "dx-rk__value is-nd" : "dx-rk__value num"}>
              {r.text}
              {r.share !== null ? <small>{`${String(Math.round(r.share * 100))}%`}</small> : null}
            </span>
          </>
        );
        return (
          <li key={r.key}>
            {r.onClick ? (
              <button type="button" className="dx-rk__row is-click" onClick={r.onClick}>
                {inner}
              </button>
            ) : (
              <div className="dx-rk__row">{inner}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** โดนัท 5 อันดับแรก + ອື່ນໆ — สีของแต่ละอันดับตรงกับแถบในรายการ */
function topSlices<T>(sorted: T[], value: (x: T) => number, label: (x: T) => string, total: number): DonutSlice[] {
  const top = sorted.slice(0, DX_PALETTE.length);
  const rest = total - top.reduce((s, x) => s + value(x), 0);
  return [
    ...top.map((x, i) => ({ label: label(x), value: value(x), color: DX_PALETTE[i] ?? OTHER_COLOR })),
    ...(rest > 0 ? [{ label: "ອື່ນໆ", value: rest, color: OTHER_COLOR }] : []),
  ];
}

/** ตัวช่วยอ่านแถวของ /stats/breakdown ที่ทุกการ์ดใช้ร่วมกัน */
export interface UnitCtx {
  rows: StatsBreakdownRow[];
  /** คำเรียกพื้นที่ลูก: ເມືອງ / ຕາແສງ / ບ້ານ */
  word: string;
  nameOf: (r: StatsBreakdownRow) => string;
  /** เจาะลงไปดู Dashboard ของพื้นที่นั้น — ไม่ส่ง = กดไม่ได้ */
  onDrill?: (id: string) => void;
  today: boolean;
  /** สิทธิ์ดูการเงินรวม (DSH-04) — false = ไม่มีคอลัมน์/ตัวเลือกค่าธรรมเนียม */
  finance: boolean;
}

type RankMetric = "requests" | "issued" | "fees";

/**
 * จัดอันดับพื้นที่ลูก — โดนัท + รายการ (mockup `rankCard`)
 * พื้นที่ที่ยังไม่เปิดใช้อยู่ท้ายรายการเป็น "ຍັງບໍ່ເປີດໃຊ້" · แถว place_id null ต้องอยู่ในอันดับด้วย
 */
export function UnitRankCard({ u, pending, limit = 10 }: { u: UnitCtx; pending: boolean; limit?: number }) {
  const [picked, setMetric] = useState<RankMetric>("requests");
  const metric = !u.finance && picked === "fees" ? "requests" : picked;
  const value = (r: StatsBreakdownRow) =>
    metric === "requests" ? r.requests : metric === "issued" ? r.issued : r.declared_fee_lak;
  const live = u.rows.filter((r) => !isIdle(r)).sort((a, b) => value(b) - value(a));
  const idle = u.rows.filter(isIdle);
  const total = live.reduce((s, r) => s + value(r), 0);
  const slices = topSlices(live.filter((r) => value(r) > 0), value, u.nameOf, total);
  const center = metric === "fees" ? moneyParts(total) : null;
  const shown = [...live, ...idle];
  const cut = shown.length > limit;
  const list = cut ? shown.slice(0, limit) : shown;
  const text = (v: number) => (metric === "fees" ? moneyText(v) : num(v));

  return (
    <DxCard
      title={`ຈັດອັນດັບ${u.word} ຕາມປະລິມານ`}
      sub={`ຫຼາຍ → ໜ້ອຍ${u.onDrill ? " · ກົດແຖວເພື່ອເບິ່ງລາຍລະອຽດ" : ""}`}
      right={
        <Segmented
          label="ເລືອກຕົວຊີ້ວັດ"
          value={metric}
          onChange={setMetric}
          options={[
            { value: "requests", label: u.today ? "ຄຳຮ້ອງໃໝ່" : "ຄຳຮ້ອງ" },
            { value: "issued", label: "ເອກະສານອອກ" },
            ...(u.finance ? [{ value: "fees" as const, label: "ຄ່າທຳນຽມ" }] : []),
          ]}
        />
      }
    >
      {pending ? (
        <p className="dx-empty">ກຳລັງໂຫຼດ…</p>
      ) : u.rows.length === 0 ? (
        <p className="dx-empty">ບໍ່ມີ{u.word}ໃນຂອບເຂດນີ້</p>
      ) : (
        <div className="dx-rank2">
          {total > 0 ? (
            <DonutChart
              key={metric}
              slices={slices}
              title={`ສັດສ່ວນ${u.word}`}
              totalLabel={center ? center.u : "ລວມ"}
              centerValue={center?.v}
              emptyLabel=""
              legend={false}
            />
          ) : (
            <p className="dx-empty">ຍັງບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້</p>
          )}
          <div>
            <RankList
              emptyLabel=""
              rows={list.map((r, i) => {
                const nd = isIdle(r);
                const v = value(r);
                return {
                  key: r.place_id ?? "unplaced",
                  label: u.nameOf(r),
                  value: nd ? null : v,
                  text: nd ? "ຍັງບໍ່ເປີດໃຊ້" : text(v),
                  share: !nd && total > 0 ? v / total : null,
                  color: v > 0 ? (DX_PALETTE[i] ?? OTHER_COLOR) : OTHER_COLOR,
                  ...(u.onDrill && r.place_id ? { onClick: () => u.onDrill?.(r.place_id as string) } : {}),
                };
              })}
            />
            {cut ? (
              <p className="dx-note">
                ສະແດງ {num(limit)} ອັນດັບທຳອິດ ຈາກ {num(shown.length)} {u.word} · ເບິ່ງຄົບໃນຕາຕະລາງລຸ່ມ
              </p>
            ) : null}
          </div>
        </div>
      )}
    </DxCard>
  );
}

/**
 * ຕາຕະລາງສະຫຼຸບ ແຍກຕາມພື້ນທີ່ລູກ + ແຖວລວມ (mockup `unitTable`)
 * แถวรวมมาจาก /stats/summary — server รับประกัน Σ แถว = summary
 */
export function UnitTable({
  u,
  here,
  level,
  summary,
  pending,
  title,
}: {
  u: UnitCtx;
  here: string;
  /** ระดับของพื้นที่ลูก — คอลัมน์ ຕາແສງເປີດໃຊ້ มีเฉพาะลูกที่เป็นเมือง/แขวง */
  level: "province" | "district" | "tasaeng" | "village";
  summary: StatsSummary | undefined;
  pending: boolean;
  title?: string;
}) {
  const rows = [...u.rows].sort(
    (a, b) => Number(isIdle(a)) - Number(isIdle(b)) || b.requests - a.requests || b.declared_fee_lak - a.declared_fee_lak,
  );
  const hasTasaengs = level === "province" || level === "district";
  const sum = (f: (r: StatsBreakdownRow) => number) => u.rows.reduce((s, r) => s + f(r), 0);
  const cols = (hasTasaengs || level === "tasaeng" ? 8 : 7) - (u.finance ? 0 : 1);
  return (
    <DxCard
      flush
      title={title ?? `ຕາຕະລາງສະຫຼຸບ ແຍກຕາມ${u.word}`}
      sub={u.onDrill ? `ກົດແຖວເພື່ອເບິ່ງ${u.word}ນັ້ນ` : undefined}
    >
      <div className="table-scroll">
        <table className="data-table dx-table">
          <thead>
            <tr>
              <th>{u.word}</th>
              {hasTasaengs ? <th className="is-right">ຕາແສງເປີດໃຊ້</th> : null}
              {level === "tasaeng" ? <th className="is-right">ບ້ານ</th> : null}
              <th className="is-right">{u.today ? "ຄຳຮ້ອງໃໝ່" : "ຈຳນວນຄຳຮ້ອງ"}</th>
              <th className="is-right">ເອກະສານອອກ</th>
              {u.finance ? <th className="is-right">ຄ່າທຳນຽມ (ກີບ)</th> : null}
              <th className="is-right" title="ລະບົບຍັງບໍ່ໄດ້ບັນທຶກຊ່ອງທາງການຍື່ນ">ຍື່ນຜ່ານແອັບ</th>
              <th className="is-right">ເວລາສະເລ່ຍ</th>
              <th className="is-right">ຄ້າງເກີນກຳນົດ</th>
            </tr>
          </thead>
          <tbody>
            {pending ? (
              <tr>
                <td colSpan={cols} className="data-table__empty">
                  ກຳລັງໂຫຼດ…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={cols} className="data-table__empty">
                  ບໍ່ມີ{u.word}ໃນຂອບເຂດນີ້
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const id = r.place_id;
                const click = u.onDrill && id ? () => u.onDrill?.(id) : undefined;
                const nd = isIdle(r);
                const dash = <span className="dx-faint">ຍັງບໍ່ເປີດໃຊ້</span>;
                return (
                  <tr
                    key={id ?? "unplaced"}
                    className={[click ? "is-click" : "", nd ? "is-nd" : ""].join(" ").trim() || undefined}
                    onClick={click}
                    tabIndex={click ? 0 : undefined}
                    onKeyDown={click ? (e) => (e.key === "Enter" ? click() : undefined) : undefined}
                  >
                    <td className="dx-table__name">
                      {u.nameOf(r)}
                      {r.villages > 0 && level !== "village" && level !== "tasaeng" ? (
                        <small className="num">{num(r.villages)} ບ້ານ</small>
                      ) : null}
                    </td>
                    {hasTasaengs ? (
                      <td className="is-right num">{r.tasaengs > 0 ? `${String(r.tasaengs_staffed)}/${String(r.tasaengs)}` : "—"}</td>
                    ) : null}
                    {level === "tasaeng" ? <td className="is-right num">{r.villages > 0 ? num(r.villages) : "—"}</td> : null}
                    <td className="is-right num">{nd ? dash : num(r.requests)}</td>
                    <td className="is-right num">{nd ? dash : num(r.issued)}</td>
                    {u.finance ? <td className="is-right num">{nd ? dash : num(r.declared_fee_lak)}</td> : null}
                    <td className="is-right dx-faint">—</td>
                    <td className="is-right">{nd ? dash : <AvgBadge seconds={r.avg_processing_seconds} />}</td>
                    <td className="is-right num">
                      {nd ? (
                        dash
                      ) : r.in_flight > 0 ? (
                        <span className={r.overdue_over_7d / r.in_flight > 0.15 ? "dx-err" : undefined}>
                          {num(r.overdue_over_7d)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {summary && !pending && rows.length > 0 ? (
            <tfoot>
              <tr className="dx-table__total">
                <td>ລວມ{here}</td>
                {hasTasaengs ? (
                  <td className="is-right num">{`${String(sum((r) => r.tasaengs_staffed))}/${String(sum((r) => r.tasaengs))}`}</td>
                ) : null}
                {level === "tasaeng" ? <td className="is-right num">{num(sum((r) => r.villages))}</td> : null}
                <td className="is-right num">{num(summary.submitted)}</td>
                <td className="is-right num">{num(summary.issued)}</td>
                {u.finance ? <td className="is-right num">{num(summary.declared_fee_lak)}</td> : null}
                <td className="is-right">—</td>
                <td className="is-right num">{duration(summary.avg_processing_seconds)}</td>
                <td className="is-right num">{num(summary.overdue_over_7d)}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <p className="dx-note dx-note--pad">
        ຍື່ນຜ່ານແອັບ: ຍັງບໍ່ມີຂໍ້ມູນ (ລະບົບຍັງບໍ່ໄດ້ບັນທຶກຊ່ອງທາງການຍື່ນ) · ເວລາສະເລ່ຍ = ຍື່ນ → ອອກເອກະສານ · ຄ້າງເກີນກຳນົດ = ເກີນ 7 ມື້
        ສະຖານະຕອນນີ້{hasTasaengs ? " · ຕາແສງເປີດໃຊ້ = ມີເຈົ້າໜ້າທີ່ປະຈຳຕາແສງແລ້ວ" : ""}
      </p>
    </DxCard>
  );
}

/** เวลาเฉลี่ย ยื่น → ออกเอกสาร — 0 = ยังไม่มีใบที่ออก ไม่ใช่ "เร็วสุด" */
export function AvgBadge({ seconds }: { seconds: number }) {
  if (seconds <= 0) return <span className="dx-faint">—</span>;
  return (
    <span
      className={seconds > SLOW_THRESHOLD_SECONDS ? "status-badge status-badge--pending" : "status-badge status-badge--success"}
    >
      <span className="num">{duration(seconds)}</span>
    </span>
  );
}

/** ชนิดเอกสาร: โดนัท 5 อันดับ + ອື່ນໆ คู่กับรายการ 8 อันดับ */
export function DocTypeCards({
  items,
  submitted,
  pending,
}: {
  items: DocTypeRow[];
  /** คำร้องทั้งหมดของช่วง (นับตามวันยื่นเหมือน /stats/document-types) — ส่วนที่เหลือคือ ປະເພດອື່ນໆ */
  submitted: number | undefined;
  pending: boolean;
}) {
  const sorted = [...items].sort((a, b) => b.requests - a.requests);
  const total = Math.max(submitted ?? 0, sorted.reduce((s, d) => s + d.requests, 0));
  const slices = topSlices(sorted, (d) => d.requests, (d) => d.name_lo, total).map((s) =>
    s.label === "ອື່ນໆ" ? { ...s, label: "ປະເພດອື່ນໆ" } : s,
  );
  const empty = pending ? "ກຳລັງໂຫຼດ…" : "ຍັງບໍ່ມີຄຳຮ້ອງໃນຊ່ວງນີ້";
  return (
    <div className="dx-two">
      <DxCard title="ປະເພດເອກະສານ" sub={`${String(DX_PALETTE.length)} ປະເພດທີ່ຍື່ນຫຼາຍທີ່ສຸດ`}>
        <DonutChart slices={slices} title="ສັດສ່ວນປະເພດເອກະສານ" totalLabel="ຄຳຮ້ອງ" emptyLabel={empty} />
      </DxCard>
      <DxCard title="ບໍລິການທີ່ຖືກຮ້ອງຂໍຫຼາຍທີ່ສຸດ" sub={`${num(sorted.length)} ອັນດັບທຳອິດ`}>
        <RankList
          emptyLabel={empty}
          rows={sorted.map((d) => ({
            key: d.document_type_id,
            label: d.name_lo,
            sub: `${d.code} · ອອກແລ້ວ ${num(d.issued)}`,
            value: d.requests,
            text: num(d.requests),
            share: total > 0 ? d.requests / total : null,
            color: "var(--success)",
          }))}
        />
      </DxCard>
    </div>
  );
}

/**
 * ເລື່ອງທີ່ຕ້ອງຕິດຕາມ — สถานะ ณ ตอนนี้ ไม่ขึ้นกับช่วงเวลาที่เลือก (ป้ายต้องบอก)
 * การ์ด 3 ใบ + ตารางแยกตามพื้นที่ลูก (เฉพาะพื้นที่ที่มีเรื่องค้างเกินกำหนดหรือถูกส่งคืน)
 */
export function FollowUpSection({
  summary,
  needsChanges,
  u,
}: {
  summary: StatsSummary | undefined;
  needsChanges: number | undefined;
  /** ไม่ส่ง = ระดับบ้าน ไม่มีพื้นที่ลูก */
  u?: UnitCtx;
}) {
  const pending = summary?.pending;
  const overdue = summary?.overdue_over_7d;
  const awaiting = summary?.awaiting_village;
  const need = (u?.rows ?? [])
    .filter((r) => r.overdue_over_7d > 0 || r.needs_changes > 0)
    .sort((a, b) => b.overdue_over_7d - a.overdue_over_7d || b.in_flight - a.in_flight);
  const shown = need.slice(0, 10);
  return (
    <div className="no-print">
      <DxSection title="ເລື່ອງທີ່ຕ້ອງຕິດຕາມ" sub="ສະຖານະຕອນນີ້ · ບໍ່ຂຶ້ນກັບຊ່ວງເວລາທີ່ເລືອກ" right={<QueueLink />} />
      <div className="dx-kpis dx-kpis--3">
        <KpiTile
          label="ກຳລັງດຳເນີນການ"
          value={pending === undefined ? undefined : num(pending)}
          unit="ລາຍການ"
          tone="navy"
          to="/requests"
          // ລໍນາຍບ້ານ (ADR-0031) ເປັນສ່ວນໜຶ່ງຂອງຍອດນີ້ ບໍ່ແມ່ນຍອດແຍກ — ຫ້າມບວກເພີ່ມ
          hint={awaiting ? `ໃນນີ້ ລໍນາຍບ້ານຢັ້ງຢືນ ${num(awaiting)}` : undefined}
        />
        <KpiTile
          label="ເກີນກຳນົດ"
          value={overdue === undefined ? undefined : num(overdue)}
          unit="ລາຍການ"
          tone="red"
          to="/requests"
          hint={
            pending && overdue !== undefined
              ? `${(Math.round((overdue / pending) * 1000) / 10).toLocaleString("lo-LA")}% ຂອງທັງໝົດ · ຄ້າງເກີນ 7 ມື້`
              : "ຄ້າງເກີນ 7 ມື້"
          }
        />
        <KpiTile
          label="ຖືກສົ່ງຄືນໃຫ້ແກ້ໄຂ"
          value={needsChanges === undefined ? undefined : num(needsChanges)}
          unit="ລາຍການ"
          tone="gold"
          to="/requests"
          hint="ລໍຖ້າຜູ້ຂໍແກ້ໄຂ"
        />
      </div>
      {u ? (
        <div className="dx-gap">
          <DxCard flush title="" sub={`ແຍກຕາມ${u.word} · ລຽງຕາມເກີນກຳນົດຫຼາຍສຸດ`}>
            <div className="table-scroll">
              <table className="data-table dx-table">
                <thead>
                  <tr>
                    <th>{u.word}</th>
                    <th className="is-right">ກຳລັງດຳເນີນການ</th>
                    <th className="is-right">ເກີນກຳນົດ</th>
                    <th className="is-right">ຖືກສົ່ງຄືນ</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="data-table__empty">
                        ບໍ່ມີລາຍການຄ້າງເກີນກຳນົດ ຫຼື ຖືກສົ່ງຄືນ
                      </td>
                    </tr>
                  ) : (
                    shown.map((r) => {
                      const id = r.place_id;
                      const click = u.onDrill && id ? () => u.onDrill?.(id) : undefined;
                      return (
                        <tr key={id ?? "unplaced"} className={click ? "is-click" : undefined} onClick={click}>
                          <td className="dx-table__name">{u.nameOf(r)}</td>
                          <td className="is-right num">{num(r.in_flight)}</td>
                          <td className="is-right num">
                            <span className={r.overdue_over_7d > 0 ? "dx-err" : "dx-faint"}>{num(r.overdue_over_7d)}</span>
                          </td>
                          <td className="is-right num">{num(r.needs_changes)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {need.length > shown.length || (u.rows.length > need.length && need.length > 0) ? (
              <p className="dx-note dx-note--pad">
                ສະແດງ {num(shown.length)} ຈາກ {num(need.length)} {u.word}ທີ່ມີເລື່ອງຄ້າງ · {num(u.rows.length - need.length)}{" "}
                {u.word}ບໍ່ມີເລື່ອງຄ້າງເກີນກຳນົດ ຫຼື ຖືກສົ່ງຄືນ
              </p>
            ) : null}
          </DxCard>
        </div>
      ) : null}
    </div>
  );
}

/** ลิงก์ "ເປີດຄິວຄຳຮ້ອງ" ข้างหัวข้อส่วน */
export function QueueLink() {
  return (
    <Link to="/requests" className="dx-sec__link">
      ເປີດຄິວຄຳຮ້ອງ <ArrowRight size={14} />
    </Link>
  );
}

